/* eslint-disable max-len, jsx-a11y/no-static-element-interactions */
/* eslint-disable jsx-a11y/anchor-is-valid, react/jsx-props-no-spreading */
import React, { useState } from 'react';
import Link from 'next/link';
import { Menu, Transition } from '@headlessui/react';
import PropTypes from 'prop-types';

import trackAction from 'utils/tracking';

import ActiveLink from 'components/Core/ActiveLink';
import IconSvg from 'components/Core/IconSvg';
import Alert from 'components/Core/Alert';

import { MenuItem, SubmenuItemsWrap } from './Menu.styles';

const NextLink = ({ url, children, activeClassName, ...rest }) => (
  <ActiveLink href="/[...slug]" as={url} activeClassName={activeClassName} passHref>
    <a {...rest}>{children}</a>
  </ActiveLink>
);

NextLink.propTypes = {
  url: PropTypes.string.isRequired,
  children: PropTypes.node.isRequired,
  className: PropTypes.string,
  activeClassName: PropTypes.string,
};

NextLink.defaultProps = {
  className: '',
  activeClassName: '',
};

const getSplitItem = (items, type) => {
  const item = items.find((i) => i.typeName === type);
  return item || null;
};

const MainMenu = ({
  menuItems,
  siteLogo,
  siteName,
  showNav,
  showSplitMenu,
  splitMenuItems,
  urgentArticle,
  preview,
}) => {
  const [isOpen, setIsOpen] = useState(false);

  const splitItemPartner = !showSplitMenu ? null : getSplitItem(splitMenuItems, 'PARTNER');
  const splitItemVisitor = !showSplitMenu ? null : getSplitItem(splitMenuItems, 'VISITOR');

  const keyUpAction = (keyevent, category, label) => {
    if (keyevent.key === 'Enter') {
      trackAction(category, label, 'click');
    }
  };

  const toggleMenuOpen = (isMenuOpen) => {
    if (isMenuOpen) {
      document.body.classList.add('modal-open');
    } else {
      document.body.classList.remove('modal-open');
    }
    setIsOpen(isMenuOpen);
  };

  const getSubMenuItems = ({ id, title, url }, index) => {
    return (
      <NextLink
        key={`menu-subitem-${id}`}
        onClick={() => {
          toggleMenuOpen(false);
          trackAction('sub-menu', title, 'click');
        }}
        onKeyUp={(e) => {
          toggleMenuOpen(false);
          keyUpAction(e, 'sub-menu', title);
        }}
        url={url}
        className={`text-base leading-5 xl:text-center xl:leading-normal xl:font-medium my-3 block border-b-2 border-transparent hover:border-primary focus:border-primary ${
          index !== 0 && 'lg:mt-4'
        }`}
        activeClassName="border-primary"
      >
        {title}
      </NextLink>
    );
  };

  const getMobileMenuItems = (menuItem, index) => {
    const { type } = menuItem;
    const isExternal = type === 'ExternalURLItemBlock';
    let id;
    let title;
    let url;
    let subItems;
    if (isExternal) {
      const { title: externalTitle, url: externalUrl } = menuItem;

      id = `external-${index}`;
      title = externalTitle;
      url = externalUrl;
      subItems = [];
    } else {
      const {
        page: { id: pageId, title: pageTitle, url: pageUrl },
        subItems: pageSubItems,
      } = menuItem;

      id = pageId;
      title = pageTitle;
      url = pageUrl;
      subItems = pageSubItems;
    }

    const externalUrlProps = isExternal ? { target: '_blank', rel: 'noopener noreferrer' } : {};

    return subItems.length ? (
      <Menu key={`mobile-menu-item-${id}`}>
        {({ open }) => (
          <div className={`relative leading-5 border-b-2 border-basic-200 ${index !== 0 && 'mt-2'}`}>
            <div className="flex w-full justify-between items-center">
              <Menu.Item
                key={`mobile-menu-subitem-${id}`}
                as={NextLink}
                {...externalUrlProps}
                onClick={() => {
                  toggleMenuOpen(false);
                  trackAction('top-menu', title, 'click');
                }}
                onKeyUp={(e) => {
                  toggleMenuOpen(false);
                  keyUpAction(e, 'top-menu', title);
                }}
                url={url}
                className={`py-3 block w-full rounded-lg transition ease-in-out duration-150 ${index !== 0 && 'mt-2'}`}
                activeClassName="text-primary"
              >
                <p className="text-base leading-6 font-bold">{title}</p>
              </Menu.Item>
              <Menu.Button
                onClick={() => {
                  trackAction('top-menu', title, 'click');
                }}
                onKeyUp={(e) => {
                  keyUpAction(e, 'top-menu', title);
                }}
                className="text-basic-700 my-3 group font-bold hover:text-basic-800 focus:outline-none focus:text-basic-800 transition ease-in-out duration-150"
              >
                <IconSvg
                  icon="arrow-down"
                  className={`${
                    open && 'u-rotate-180'
                  } text-primary h-3 w-3 ml-2 group-hover:text-primary-600 group-focus:text-primary-600 transition ease-in-out duration-150`}
                />
              </Menu.Button>
            </div>
            {open && (
              <Transition
                show={open}
                enter="transition ease-out duration-200"
                enterFrom="transform opacity-0 scale-95"
                enterTo="transform opacity-100 scale-100"
                leave="transition ease-in duration-200"
                leaveFrom="transform opacity-100 scale-100"
                leaveTo="transform opacity-0 scale-95"
              >
                <Menu.Items static>
                  <div className="overflow-hidden">
                    <div className="bg-white px-5 py-2">{subItems.map(getSubMenuItems)}</div>
                  </div>
                </Menu.Items>
              </Transition>
            )}
          </div>
        )}
      </Menu>
    ) : (
      <Menu.Item
        key={`mobile-menu-subitem-${id}`}
        as={NextLink}
        {...externalUrlProps}
        onClick={() => {
          toggleMenuOpen(false);
          trackAction('top-menu', title, 'click');
        }}
        onKeyUp={(e) => {
          toggleMenuOpen(false);
          keyUpAction(e, 'top-menu', title);
        }}
        url={url}
        className={`py-3 block border-b-2 border-basic-200 rounded-lg transition ease-in-out duration-150 ${
          index !== 0 && 'mt-2'
        }`}
        activeClassName="text-primary"
      >
        <p className="text-base leading-6 font-bold">{title}</p>
      </Menu.Item>
    );
  };

  const getMenuItems = (menuItem, index) => {
    const { type } = menuItem;
    const isExternal = type === 'ExternalURLItemBlock';
    let id;
    let title;
    let url;
    let subItems;
    if (isExternal) {
      const { title: externalTitle, url: externalUrl } = menuItem;

      id = externalUrl;
      title = externalTitle;
      url = externalUrl;
      subItems = [];
    } else {
      const {
        page: { id: pageId, title: pageTitle, url: pageUrl },
        subItems: pageSubItems,
      } = menuItem;

      id = pageId;
      title = pageTitle;
      url = pageUrl;
      subItems = pageSubItems;
    }
    const externalUrlProps = isExternal ? { target: '_blank', rel: 'noopener noreferrer' } : {};

    return subItems.length ? (
      <MenuItem className="group h-full" key={`menu-item-${id}`}>
        <div className={`z-20 h-full relative leading-5 ${index !== 0 && 'lg:ml-4 xxl:ml-6'}`}>
          <div className="flex items-center h-full">
            <ActiveLink key={`menu-item-${id}`} href="/[...slug]" as={url} passHref activeClassName="border-primary">
              <a
                {...externalUrlProps}
                onClick={() => {
                  trackAction('top-menu', title, 'click');
                }}
                onKeyUp={(e) => {
                  keyUpAction(e, 'top-menu', title);
                }}
                className={`h6 xl:h5 py-2 border-b-2 border-transparent font-bold break-normal text-basic-700 hover:border-primary focus:outline-none focus:border-primary transition-color ease-in-out duration-150 ${
                  index !== 0 && 'lg:ml-4 xxl:ml-6'
                }`}
              >
                {title}
              </a>
            </ActiveLink>
            <IconSvg
              onMouseOver={() => {
                trackAction('top-menu', title, 'hover');
              }}
              icon="arrow-down"
              className="transition-transform ease-in-out duration-150 transform group-hover:rotate-180 text-primary h-3 w-3 ml-2"
            />
          </div>
          <div className="submenu-items hidden absolute -ml-4 -mt-2 transform px-2 min-w-180 max-w-md left-1/2 sm:px-0 lg:ml-0 lg:-translate-x-1/2 focus:outline-none">
            <div className="rounded-lg border-b-2 border-basic-300">
              <div className="rounded-lg overflow-hidden focus:outline-none">
                <SubmenuItemsWrap className="z-20 relative bg-white px-5 py-2 lg:px-6 focus:outline-none">
                  {subItems.map(getSubMenuItems)}
                </SubmenuItemsWrap>
              </div>
            </div>
          </div>
        </div>
      </MenuItem>
    ) : (
      <ActiveLink key={`menu-item-${id}`} href="/[...slug]" as={url} passHref activeClassName="border-primary">
        <a
          {...externalUrlProps}
          onClick={() => {
            trackAction('top-menu', title, 'click');
          }}
          onKeyUp={(e) => {
            keyUpAction(e, 'top-menu', title);
          }}
          className={`h6 xl:h5 py-2 border-b-2 border-transparent font-bold break-normal text-basic-700 hover:border-primary focus:outline-none focus:border-primary transition-color ease-in-out duration-150 ${
            index !== 0 && 'lg:ml-4 xxl:ml-6'
          }`}
        >
          {title}
        </a>
      </ActiveLink>
    );
  };

  return (
    <>
      <div
        className={`bg-white border-b-3 border-basic-300 fixed top-0 left-0 right-0 z-50 ${
          isOpen && 'bottom-0 overflow-y-scroll'
        }`}
      >
        {preview.token && <Alert preview={preview} />}
        {urgentArticle && (
          <div className="bg-basic-700 p-1 md:p-2 flex justify-center">
            <Link href="[...slug]" as={urgentArticle.url} passHref>
              <a className="lg:text-lg text-white underline font-bold text-center">{urgentArticle.title}</a>
            </Link>
          </div>
        )}
        {showSplitMenu && (
          <div className="w-full bg-basic-200 border-basic-300 border-b-3 hidden lg:block">
            <div className="container mx-auto flex -mb-3px px-4">
              {splitItemVisitor && (
                <ActiveLink
                  href="/[...slug]"
                  as={splitItemVisitor.url}
                  activeClassName="border-primary opacity-100 border-b-2"
                  checkForNested
                  passHref
                >
                  <a
                    className="h6 opacity-50 leading-5 font-bold break-normal text-basic-700 hover:text-basic-800 focus:outline-none focus:text-basic-800 transition ease-in-out duration-150 py-2"
                    onClick={() => {
                      trackAction('b2c-b2b', 'Bezoeker', 'click');
                    }}
                    onKeyUp={(e) => {
                      keyUpAction(e, 'b2c-b2b', 'Bezoeker');
                    }}
                  >
                    {splitItemVisitor.title}
                  </a>
                </ActiveLink>
              )}

              {splitItemPartner && (
                <ActiveLink
                  href="/[...slug]"
                  as={splitItemPartner.url}
                  activeClassName="border-primary opacity-100 border-b-2"
                  checkForNested
                  passHref
                >
                  <a
                    className="ml-4 h6 opacity-50 leading-5 font-bold break-normal text-basic-700 hover:text-basic-800 focus:outline-none focus:text-basic-800 transition ease-in-out duration-150 py-2"
                    onClick={() => {
                      trackAction('b2c-b2b', 'Partner', 'click');
                    }}
                    onKeyUp={(e) => {
                      keyUpAction(e, 'b2c-b2b', 'Partner');
                    }}
                  >
                    {splitItemPartner.title}
                  </a>
                </ActiveLink>
              )}
            </div>
          </div>
        )}
        <div className="container mx-auto px-4">
          <div className="flex justify-between items-center md:p-0 h-16 lg:h-20 xxl:h-24">
            <div className="flex-1/2 max-w-1/2 lg:flex-1/6 lg:max-w-1/6 h-full">
              <Link href="/" passHref>
                <a className="flex items-center h-full" aria-label={siteName} alt={siteName}>
                  <img
                    className="w-auto max-h-full md:hidden h-auto py-2.5"
                    src={siteLogo.sm.url}
                    alt={siteLogo.title}
                  />
                  <img
                    className="w-auto max-h-full hidden md:block h-auto py-2.5"
                    src={siteLogo.md.url}
                    alt={siteLogo.title}
                  />
                </a>
              </Link>
            </div>
            {showNav && (
              <div className="flex-1 pl-4 lg:hidden flex justify-end">
                {/* TODO: Login screen */}
                {/* <button
                  type="button"
                  className="inline-flex flex-col items-center justify-center p-2 rounded-md text-basic-700 hover:text-basic-800 focus:outline-none transition duration-150 ease-in-out"
                >
                  <IconSvg icon="account" className="text-primary h-8 w-8" />
                  <span className="text-tiny leading-3">Account</span>
                </button> */}
                <Menu>
                  <>
                    <button
                      type="button"
                      onClick={() => {
                        toggleMenuOpen(true);
                      }}
                      className="inline-flex flex-col items-center justify-center p-2 ml-4 rounded-md text-basic-700 hover:text-basic-800 focus:outline-none transition duration-150 ease-in-out"
                    >
                      <IconSvg icon="menu" className="text-primary h-8 w-8" />
                      <span className="text-tiny leading-3">Menu</span>
                    </button>
                    <Transition
                      show={isOpen}
                      enter="transition-opacity duration-100 ease-out"
                      enterFrom="opacity-0"
                      enterTo="opacity-100"
                      leave="transition-opacity duration-75 ease-in"
                      leaveFrom="opacity-100"
                      leaveTo="opacity-0"
                    >
                      <Menu.Items static className="absolute inset-0 origin-top-right xl:hidden">
                        <div className="min-h-screen bg-white">
                          <div className="pt-5 pb-6 px-5">
                            <div className="flex items-center justify-between">
                              <div>
                                <img
                                  className="h-8 w-auto sm:h-10 md:hidden"
                                  src={siteLogo.sm.url}
                                  alt={siteLogo.title}
                                />
                                <img
                                  className="h-8 w-auto sm:h-10 hidden md:block"
                                  src={siteLogo.md.url}
                                  alt={siteLogo.title}
                                />
                              </div>
                              <div className="-mr-2">
                                <button
                                  type="button"
                                  onClick={() => {
                                    toggleMenuOpen(false);
                                  }}
                                  className="inline-flex items-center justify-center p-2 rounded-md focus:outline-none  transition duration-150 ease-in-out"
                                >
                                  <IconSvg icon="close" className="text-primary h-6 w-6" />
                                </button>
                              </div>
                            </div>
                            <div className="mt-6">
                              {showSplitMenu && (
                                <div className="flex">
                                  {splitItemVisitor && (
                                    <ActiveLink
                                      href="/[...slug]"
                                      as={splitItemVisitor.url}
                                      activeClassName="border-primary opacity-100 border-b-2"
                                      checkForNested
                                      passHref
                                    >
                                      <a
                                        className="text-lg opacity-50 leading-5 font-bold break-normal text-basic-700 hover:text-basic-800 focus:outline-none focus:text-basic-800 transition ease-in-out duration-150"
                                        onClick={() => {
                                          toggleMenuOpen(false);
                                          trackAction('b2c-b2b', 'Bezoeker', 'click');
                                        }}
                                        onKeyUp={(e) => {
                                          toggleMenuOpen(false);
                                          keyUpAction(e, 'b2c-b2b', 'Bezoeker');
                                        }}
                                      >
                                        {splitItemVisitor.title}
                                      </a>
                                    </ActiveLink>
                                  )}

                                  {splitItemPartner && (
                                    <ActiveLink
                                      href="/[...slug]"
                                      as={splitItemPartner.url}
                                      activeClassName="border-primary opacity-100 border-b-2"
                                      checkForNested
                                      passHref
                                    >
                                      <a
                                        className="ml-4 text-lg opacity-50 leading-5 font-bold break-normal text-basic-700 hover:text-basic-800 focus:outline-none focus:text-basic-800 transition ease-in-out duration-150"
                                        onClick={() => {
                                          toggleMenuOpen(false);
                                          trackAction('b2c-b2b', 'Partner', 'click');
                                        }}
                                        onKeyUp={(e) => {
                                          toggleMenuOpen(false);
                                          keyUpAction(e, 'b2c-b2b', 'Partner');
                                        }}
                                      >
                                        {splitItemPartner.title}
                                      </a>
                                    </ActiveLink>
                                  )}
                                </div>
                              )}
                              <nav className="mt-6">{menuItems.map(getMobileMenuItems)}</nav>
                            </div>
                          </div>
                        </div>
                      </Menu.Items>
                    </Transition>
                  </>
                </Menu>
              </div>
            )}
            {showNav && (
              <nav className="hidden h-full lg:pl-6 lg:flex flex-1 items-center justify-end">
                {menuItems.map(getMenuItems)}
              </nav>
            )}
            <div className="hidden xl:block">
              {/* TODO: Login screen */}
              {/* <Cta>Log in</Cta> */}
            </div>
          </div>
        </div>
      </div>
    </>
  );
};

MainMenu.propTypes = {
  siteName: PropTypes.string,
  siteLogo: PropTypes.shape({
    sm: PropTypes.shape({
      url: PropTypes.string.isRequired,
    }),
    md: PropTypes.shape({
      url: PropTypes.string.isRequired,
    }),
    title: PropTypes.string.isRequired,
  }),
  showSplitMenu: PropTypes.bool,
  splitMenuItems: PropTypes.arrayOf(
    PropTypes.shape({
      id: PropTypes.string.isRequired,
      url: PropTypes.string.isRequired,
      title: PropTypes.string.isRequired,
      typeName: PropTypes.string.isRequired,
    }),
  ),
  menuItems: PropTypes.arrayOf(
    PropTypes.oneOfType([
      PropTypes.shape({
        type: PropTypes.string.isRequired,
        page: PropTypes.shape({
          id: PropTypes.string.isRequired,
          title: PropTypes.string.isRequired,
          url: PropTypes.string.isRequired,
        }),
        subItems: PropTypes.arrayOf(
          PropTypes.shape({
            id: PropTypes.string.isRequired,
            title: PropTypes.string.isRequired,
            url: PropTypes.string.isRequired,
          }),
        ),
      }),
      PropTypes.shape({
        type: PropTypes.string.isRequired,
        title: PropTypes.string.isRequired,
        url: PropTypes.string.isRequired,
      }),
    ]),
  ),
  showNav: PropTypes.bool,
  urgentArticle: PropTypes.shape({
    title: PropTypes.string,
    url: PropTypes.string,
  }),
  preview: PropTypes.shape({
    token: PropTypes.string,
    id: PropTypes.string,
    contentType: PropTypes.string,
  }),
};

MainMenu.defaultProps = {
  siteName: '',
  siteLogo: {
    title: 'Sportfondsen logo',
    sm: {
      url: '/assets/logo_sportfondsen.png',
    },
    md: {
      url: '/assets/logo_sportfondsen.png',
    },
  },
  menuItems: [],
  showNav: true,
  showSplitMenu: false,
  splitMenuItems: [],
  urgentArticle: {
    title: '',
    url: '',
  },
  preview: {
    token: null,
  },
};

export default MainMenu;
